import { ImageContext } from '@v2/components/imageHandling/types/types';
import { useUpdateCurrentImageContext } from './useUpdateCurrentImageContext';
import { useImageHandlingViewModel } from '@v2/components/imageHandling/viewmodels/useImageHandlingViewModel';

/**
 * Combined hook that encapsulates image handling view model with context updates.
 *
 * This hook combines:
 * - useUpdateCurrentImageContext: Updates the current context key in the store
 * - useImageHandlingViewModel: Provides image documents state and actions
 *
 * @param imageContext - The image context containing scopeId/scopeReferenceId or documentId
 * @returns Image handling state and actions
 */
export const useImageHandlingViewModelWithContextUpdate = ({
  imageContext,
  retrieveAllDocuments = true,
}: {
  imageContext: ImageContext;
  retrieveAllDocuments?: boolean;
}) => {
  // Update the current context key in the store
  useUpdateCurrentImageContext(imageContext);

  // Get image documents state and actions
  const {
    state: { imagesDocuments, selectedIndex, setSelectedIndex, loading, error },
    actions: { loadImagesDocuments, removeImageDocument, rotateImageDocument, cropImageDocument },
  } = useImageHandlingViewModel({ imageContext, retrieveAllDocuments });

  return {
    state: {
      imagesDocuments,
      selectedIndex,
      setSelectedIndex,
      loading,
      error,
    },
    actions: {
      loadImagesDocuments,
      removeImageDocument,
      rotateImageDocument,
      cropImageDocument,
    },
  };
};
